function buildApiUrl(root, path) {
  const normalizedRoot = String(root || '/wp-json/').replace(/\/+$/, '');
  const normalizedPath = String(path || '').replace(/^\/+/, '');

  return `${normalizedRoot}/digivibe-savora/v1/${normalizedPath}`;
}

async function postJson(root, nonce, path, payload = {}) {
  const response = await fetch(buildApiUrl(root, path), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': nonce,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error('The request could not be completed.');
  }

  const json = await response.json();

  return json.data || json;
}

function renderValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'Not available';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return String(value);
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function normalizeData(data) {
  return {
    privacy: data.privacy || data.consent || {},
    account: data.account || {},
    consent: data.consent || {},
    activity: data.activity || {},
    reports: Array.isArray(data.reports) ? data.reports : [],
    answers: Array.isArray(data.answers) ? data.answers : [],
  };
}

function renderPrivacySection(data) {
  const { privacy, consent } = normalizeData(data);

  const acceptedVersion =
    privacy.accepted_version || consent.privacy_version || consent.accepted_version || '';

  const acceptedAt =
    privacy.accepted_at || consent.privacy_accepted_at || consent.accepted_at || '';

  const currentVersion = privacy.current_version || '';
  const currentPolicyText = privacy.current_policy_text || '';
  const showPolicy = Boolean(data.showPolicy);

  const hasPrivacyData = acceptedVersion !== '' || acceptedAt !== '' || currentVersion !== '';

  const needsAcceptance =
    privacy.needs_acceptance === true ||
    privacy.needs_acceptance === 'true' ||
    (acceptedVersion !== '' && currentVersion !== '' && acceptedVersion !== currentVersion);

  return `
    <section class="dv-card dv-privacy-card dv-privacy-card--wide">
      <header class="dv-privacy-card__header">
        <h3>Privacy Policy</h3>
        <p>Review the privacy policy version linked to your account.</p>
      </header>

      <dl class="dv-privacy-list">
        <div>
          <dt>Accepted version</dt>
          <dd>${escapeHtml(renderValue(acceptedVersion))}</dd>
        </div>

        <div>
          <dt>Accepted at</dt>
          <dd>${escapeHtml(renderValue(acceptedAt))}</dd>
        </div>

        <div>
          <dt>Current version</dt>
          <dd>${escapeHtml(renderValue(currentVersion))}</dd>
        </div>
      </dl>

      <div class="dv-privacy-actions">
        <button type="button" class="dv-button" data-privacy-action="toggle-policy">
          ${showPolicy ? 'Hide privacy policy' : 'Read privacy policy'}
        </button>

        ${
          needsAcceptance
            ? `
              <button type="button" class="dv-button" data-privacy-action="accept-policy">
                Accept the new privacy policy
              </button>
            `
            : hasPrivacyData
              ? `
                <p class="dv-privacy-dashboard__notice">
                  You accepted the current privacy policy.
                </p>
              `
              : `
                <p class="dv-privacy-dashboard__notice">
                  Privacy policy status is loading.
                </p>
              `
        }
      </div>

      ${
        showPolicy
          ? `
            <div class="dv-privacy-policy-text" aria-live="polite">
              <h4>Current privacy policy</h4>
              <div class="dv-privacy-policy-text__body">
                ${escapeHtml(currentPolicyText)}
              </div>
            </div>
          `
          : ''
      }
    </section>
  `;
}

function renderMyDataSection(data, hasLoadedData) {
  const normalized = normalizeData(data || {});
  const { account, consent, activity, reports, answers } = normalized;

  return `
    <section class="dv-card dv-privacy-card dv-privacy-card--wide">
      <header class="dv-privacy-card__header">
        <h3>My Data</h3>
        <p>Load, download or delete the personal Savora data linked to your account.</p>
      </header>

      <div class="dv-privacy-actions">
        <button type="button" class="dv-button" data-privacy-action="load-data">
          Load my Savora data
        </button>

        <button
          type="button"
          class="dv-button dv-button--secondary"
          data-privacy-action="download-pdf"
          ${hasLoadedData ? '' : 'disabled'}
        >
          Download as PDF
        </button>

        <button type="button" class="dv-button dv-button--danger" data-privacy-action="delete-data">
          Delete my Savora data
        </button>
      </div>

      ${
        hasLoadedData
          ? `
            <div class="dv-privacy-dashboard__grid">
              <article class="dv-card dv-privacy-card">
                <h4>Account</h4>
                <dl class="dv-privacy-list">
                  <div><dt>Name</dt><dd>${escapeHtml(renderValue(account.display_name))}</dd></div>
                  <div><dt>Email</dt><dd>${escapeHtml(renderValue(account.email))}</dd></div>
                  <div><dt>Registered</dt><dd>${escapeHtml(renderValue(account.registered_at))}</dd></div>
                  <div><dt>Roles</dt><dd>${escapeHtml(renderValue((account.roles || []).join(', ')))}</dd></div>
                </dl>
              </article>

              <article class="dv-card dv-privacy-card">
                <h4>Consent</h4>
                <dl class="dv-privacy-list">
                  <div><dt>Accepted</dt><dd>${escapeHtml(renderValue(consent.privacy_accepted))}</dd></div>
                  <div><dt>Accepted at</dt><dd>${escapeHtml(renderValue(consent.privacy_accepted_at))}</dd></div>
                  <div><dt>Version</dt><dd>${escapeHtml(renderValue(consent.privacy_version))}</dd></div>
                </dl>
              </article>

              <article class="dv-card dv-privacy-card">
                <h4>Activity</h4>
                <dl class="dv-privacy-list">
                  <div><dt>Last login</dt><dd>${escapeHtml(renderValue(activity.last_login))}</dd></div>
                  <div><dt>Last access</dt><dd>${escapeHtml(renderValue(activity.last_access))}</dd></div>
                </dl>
              </article>

              <article class="dv-card dv-privacy-card">
                <h4>Summary</h4>
                <dl class="dv-privacy-list">
                  <div><dt>Answer sets</dt><dd>${answers.length}</dd></div>
                  <div><dt>Report sets</dt><dd>${reports.length}</dd></div>
                </dl>
              </article>
            </div>
          `
          : ''
      }
    </section>
  `;
}

function renderDashboard(root, state) {
  const { data, hasLoadedData, isLoading, message, error } = state;

  root.innerHTML = `
    <section class="dv-privacy-dashboard">
      <header class="dv-privacy-dashboard__header">
        <h2>Privacy Dashboard</h2>
        <p>Review the privacy policy and manage the personal data linked to your Savora account.</p>
      </header>

      ${renderPrivacySection({
        ...(data || {}),
        showPolicy: state.showPolicy,
      })}

      ${renderMyDataSection(data, hasLoadedData)}

      <p class="dv-privacy-dashboard__status" aria-live="polite">
        ${escapeHtml(error || message || (isLoading ? 'Loading…' : ''))}
      </p>
    </section>
  `;

  bindDashboardEvents(root, state);
}

function bindDashboardEvents(root, state) {
  const apiRoot = state.apiRoot;
  const nonce = state.nonce;

  root.querySelector('[data-privacy-action="toggle-policy"]')?.addEventListener('click', () => {
    state.showPolicy = !state.showPolicy;
    renderDashboard(root, state);
  });

  root.querySelector('[data-privacy-action="load-data"]')?.addEventListener('click', async () => {
    state.isLoading = true;
    state.message = 'Loading your data…';
    state.error = '';
    renderDashboard(root, state);

    try {
      const data = await postJson(apiRoot, nonce, 'privacy/export');
      state.data = data;
      state.hasLoadedData = true;
      state.message = 'Your data has been loaded.';
    } catch (error) {
      state.error = error.message || 'Could not load your Savora data.';
    } finally {
      state.isLoading = false;
      renderDashboard(root, state);
    }
  });

  root
    .querySelector('[data-privacy-action="accept-policy"]')
    ?.addEventListener('click', async () => {
      state.isLoading = true;
      state.message = 'Accepting the latest privacy policy…';
      state.error = '';
      renderDashboard(root, state);

      try {
        const data = await postJson(apiRoot, nonce, 'privacy/accept');
        state.data = data;
        state.message = 'The latest privacy policy has been accepted.';
      } catch (error) {
        state.error = error.message || 'Could not accept the privacy policy.';
      } finally {
        state.isLoading = false;
        renderDashboard(root, state);
      }
    });

  root.querySelector('[data-privacy-action="download-pdf"]')?.addEventListener('click', () => {
    window.print();
  });

  root.querySelector('[data-privacy-action="delete-data"]')?.addEventListener('click', async () => {
    const confirmed = window.confirm(
      'This will permanently delete your Savora data. This action cannot be undone.'
    );

    if (!confirmed) {
      return;
    }

    state.isLoading = true;
    state.message = 'Deleting your data…';
    state.error = '';
    renderDashboard(root, state);

    try {
      await postJson(apiRoot, nonce, 'privacy/delete');
      state.data = {};
      state.hasLoadedData = false;
      state.message = 'Your Savora data has been deleted.';
    } catch (error) {
      state.error = error.message || 'Could not delete your Savora data.';
    } finally {
      state.isLoading = false;
      renderDashboard(root, state);
    }
  });
}

function initPrivacyDashboard(root) {
  const state = {
    apiRoot: root.dataset.wpApiRoot || '/wp-json/',
    nonce: root.dataset.wpNonce || '',
    data: {},
    hasLoadedData: false,
    showPolicy: false,
    isLoading: true,
    message: 'Loading privacy policy status…',
    error: '',
  };

  renderDashboard(root, state);

  postJson(state.apiRoot, state.nonce, 'privacy/export')
    .then((data) => {
      state.data = data;
      state.message = '';
      state.error = '';
    })
    .catch((error) => {
      state.error = error.message || 'Could not load privacy policy status.';
    })
    .finally(() => {
      state.isLoading = false;
      renderDashboard(root, state);
    });
}

document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelectorAll('.digivibe-privacy-dashboard-root[data-privacy-dashboard="1"]')
    .forEach(initPrivacyDashboard);
});
